import { useApp } from '@/contexts';

import React, { useEffect, useMemo, useState } from 'react';

import { useGetEnvironmentsQuery } from '@features/environments';
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Error as ErrorIcon,
  OpenInNew as OpenInNewIcon,
  Percent as PercentIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Stop as StopIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format, startOfWeek, subHours, subWeeks } from 'date-fns';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { useAppDispatch } from '@/app/hooks';
import { InlineLoader, SkeletonLoader } from '@/components/common';
import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import { PageContainer, PageHeading } from '@/components/layout';
import type { ExecutionLog } from '@/types';
import { logger } from '@/utils/logger';

import {
  transformersApi,
  useGetExecutionCountQuery,
  useGetTransformerLogsQuery,
  useGetTransformersQuery,
  useGetWorkspaceExecutionCountQuery,
  useGetWorkspaceExecutionsQuery,
  useStopExecutionMutation,
} from '../api/transformersApi';
import { ExecutionStatusIcon } from '../components/ExecutionStatusIcon';
import { LogResultTable } from '../components/LogResultTable';
import type { ExecutionStatus } from '../utils/executionStatus';
import { getExecutionStatus } from '../utils/executionStatus';

type TimeFilter = 'hour' | 'day' | 'week' | 'pastWeek' | 'all' | 'custom';
type StatusFilter = 'all' | 'success' | 'error' | 'partial' | 'running';
type SortField = 'startTime' | 'name' | 'environmentId' | 'timer' | 'recordsRead' | 'status';
type SortOrder = 'asc' | 'desc';

const STATUS_SORT_RANK: Record<ExecutionStatus, number> = {
  running: 0,
  error: 1,
  partial: 2,
  success: 3,
};

// Maps the UI status filter to the backend `state` keyword used by the
// transformer API (matches legacy jobsFilter keys).
const STATUS_TO_SERVER_STATE: Record<StatusFilter, string> = {
  all: 'all',
  success: 'success',
  error: 'error',
  partial: 'partialSuccess',
  running: 'progress',
};

/**
 * Executions list page - shows all execution runs for an app
 */
export const ExecutionsListPage = () => {
  const { appId, tenantId } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { showSnackbar } = useSnackbar();

  // Stop execution mutation
  const [stopExecution, { isLoading: isStoppingExecution }] = useStopExecutionMutation();

  // Get recipe filter from query params (?recipe=8212)
  const recipeFilterFromUrl = searchParams.get('recipe');
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const activeRecipeFilter = recipeFilterFromUrl || selectedRecipeId;

  const [timeFilter, setTimeFilter] = useState<TimeFilter>(recipeFilterFromUrl ? 'all' : 'day');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortField, setSortField] = useState<SortField>('startTime');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [customDateRange, setCustomDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({ startDate: null, endDate: null });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsManualRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsManualRefreshing(false);
    }
  };

  // When recipe filter from URL changes, set time filter to 'all'
  useEffect(() => {
    if (recipeFilterFromUrl) {
      setTimeFilter('all');
    }
  }, [recipeFilterFromUrl]);

  // Fetch recipes for autocomplete filter
  const { data: transformers } = useGetTransformersQuery(appId!, { skip: !appId });

  // Sort transformers alphabetically by name
  const sortedTransformers = useMemo(() => {
    if (!transformers) return [];
    return [...transformers].sort((a, b) => a.name.localeCompare(b.name));
  }, [transformers]);

  const activeRecipeName = useMemo(() => {
    if (!activeRecipeFilter || !transformers) return undefined;
    return transformers.find((t) => String(t.baseId) === activeRecipeFilter)?.name;
  }, [activeRecipeFilter, transformers]);

  // Fetch environments for the app
  const { data: environments } = useGetEnvironmentsQuery(
    {
      tenantId,
      appId: appId!,
    },
    { skip: !tenantId || !appId }
  );

  const serverMode = timeFilter === 'all';
  const serverState = STATUS_TO_SERVER_STATE[statusFilter];
  const offset = page * rowsPerPage;
  const usingRecipeEndpoint = serverMode && !!activeRecipeFilter;

  const workspaceQueryArgs = useMemo(() => {
    if (serverMode) {
      return { state: serverState, limit: rowsPerPage, offset };
    }
    const durationMap: Record<TimeFilter, string> = {
      hour: 'pastHour',
      day: 'past24Hours',
      week: 'thisWeek',
      pastWeek: 'pastWeek',
      custom: 'dateRange',
      all: '',
    };
    const duration = durationMap[timeFilter];
    if (timeFilter === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      return {
        duration,
        state: 'all',
        startTime: customDateRange.startDate.toISOString(),
        endTime: customDateRange.endDate.toISOString(),
      };
    }
    return duration ? { duration, state: 'all' } : { state: 'all' };
  }, [
    serverMode,
    serverState,
    rowsPerPage,
    offset,
    timeFilter,
    customDateRange.startDate,
    customDateRange.endDate,
  ]);

  const pollingInterval = autoRefresh ? 10000 : 0;

  const {
    data: workspaceData,
    isLoading: workspaceLoading,
    error: workspaceError,
    refetch: refetchWorkspace,
  } = useGetWorkspaceExecutionsQuery(
    { tenantId, appId: appId!, ...workspaceQueryArgs },
    {
      skip: !tenantId || !appId || usingRecipeEndpoint,
      pollingInterval,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: recipeData,
    isLoading: recipeLoading,
    error: recipeError,
    refetch: refetchRecipe,
  } = useGetTransformerLogsQuery(
    {
      tenantId,
      transBaseId: activeRecipeFilter ?? '',
      state: serverState,
      limit: rowsPerPage,
      offset,
    },
    { skip: !tenantId || !usingRecipeEndpoint, pollingInterval, refetchOnMountOrArgChange: true }
  );

  const { data: workspaceCount } = useGetWorkspaceExecutionCountQuery(
    { tenantId, appId: appId! },
    { skip: !tenantId || !appId || !serverMode || usingRecipeEndpoint }
  );
  const { data: recipeCount } = useGetExecutionCountQuery(
    { tenantId, appId: appId!, transId: activeRecipeFilter ?? '' },
    { skip: !tenantId || !appId || !usingRecipeEndpoint }
  );

  const executions = usingRecipeEndpoint ? recipeData : workspaceData;
  const isLoading = usingRecipeEndpoint ? recipeLoading : workspaceLoading;
  const error = usingRecipeEndpoint ? recipeError : workspaceError;
  const refetch = usingRecipeEndpoint ? refetchRecipe : refetchWorkspace;

  useEffect(() => {
    setPage(0);
  }, [timeFilter, statusFilter, activeRecipeFilter]);

  // Filter executions by status, recipe, and time (client-side)
  const filteredExecutions = useMemo(() => {
    if (!executions) return [];

    let filtered = executions;

    // Client-side time filtering (in case API doesn't filter correctly)
    if (timeFilter === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      filtered = filtered.filter((exec) => {
        if (!exec.startTime) return false;
        const execDate = new Date(exec.startTime);
        return execDate >= customDateRange.startDate! && execDate <= customDateRange.endDate!;
      });
    } else if (timeFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter((exec) => {
        if (!exec.startTime) return false;
        const execDate = new Date(exec.startTime);
        switch (timeFilter) {
          case 'hour':
            return execDate >= subHours(now, 1);
          case 'day':
            return execDate >= subHours(now, 24);
          case 'week':
            return execDate >= startOfWeek(now);
          case 'pastWeek':
            return execDate >= subWeeks(now, 1);
          default:
            return true;
        }
      });
    }

    if (!serverMode && activeRecipeFilter) {
      filtered = filtered.filter((exec) => {
        const baseId = exec.baseId || exec.executionMetadata?.baseId;
        return String(baseId) === activeRecipeFilter;
      });
    }

    if (!serverMode && statusFilter !== 'all') {
      filtered = filtered.filter((exec) => {
        const { status } = getExecutionStatus(exec);
        return status === statusFilter;
      });
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (sortField) {
        case 'startTime':
          aVal = a.startTime ? new Date(a.startTime).getTime() : 0;
          bVal = b.startTime ? new Date(b.startTime).getTime() : 0;
          break;
        case 'name':
          aVal = (a.executionMetadata?.recipeName || a.name || '').toLowerCase();
          bVal = (b.executionMetadata?.recipeName || b.name || '').toLowerCase();
          break;
        case 'environmentId':
          aVal = a.environmentId || '';
          bVal = b.environmentId || '';
          break;
        case 'timer':
          aVal = a.timer || 0;
          bVal = b.timer || 0;
          break;
        case 'recordsRead':
          aVal = a.recordsRead || 0;
          bVal = b.recordsRead || 0;
          break;
        case 'status':
          aVal = STATUS_SORT_RANK[getExecutionStatus(a).status];
          bVal = STATUS_SORT_RANK[getExecutionStatus(b).status];
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [
    executions,
    serverMode,
    statusFilter,
    activeRecipeFilter,
    timeFilter,
    sortField,
    sortOrder,
    customDateRange.startDate,
    customDateRange.endDate,
  ]);

  const paginatedExecutions = useMemo(() => {
    if (serverMode) return filteredExecutions;
    const start = page * rowsPerPage;
    return filteredExecutions.slice(start, start + rowsPerPage);
  }, [serverMode, filteredExecutions, page, rowsPerPage]);

  const hasUnknownTotal = serverMode && statusFilter !== 'all';
  const serverTotal = (usingRecipeEndpoint ? recipeCount?.total : workspaceCount?.total) ?? 0;
  let totalCount = filteredExecutions.length;
  if (serverMode) {
    totalCount = hasUnknownTotal ? -1 : serverTotal;
  }
  const isLastServerPage = hasUnknownTotal && paginatedExecutions.length < rowsPerPage;

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Compute execution summary statistics from filtered data
  const stats = useMemo(() => {
    let successCount = 0;
    let failedCount = 0;
    let partialCount = 0;
    let runningCount = 0;
    let totalDuration = 0;
    let durationCount = 0;

    for (const exec of filteredExecutions) {
      const { status } = getExecutionStatus(exec);
      switch (status) {
        case 'success':
          successCount++;
          break;
        case 'error':
          failedCount++;
          break;
        case 'partial':
          partialCount++;
          break;
        case 'running':
          runningCount++;
          break;
      }
      if (exec.timer != null && exec.timer > 0) {
        totalDuration += exec.timer;
        durationCount++;
      }
    }

    const total = filteredExecutions.length;
    const completed = successCount + failedCount + partialCount;
    const successRate = completed > 0 ? (successCount / completed) * 100 : 0;
    const avgDuration = durationCount > 0 ? totalDuration / durationCount : 0;

    return {
      total,
      successCount,
      failedCount,
      partialCount,
      runningCount,
      successRate,
      avgDuration,
    };
  }, [filteredExecutions]);

  const handleRecipeClick = (execution: ExecutionLog) => {
    const baseId = execution.baseId || execution.executionMetadata?.baseId || activeRecipeFilter;
    if (!baseId) {
      logger.error('Cannot navigate to recipe: baseId is undefined', {
        execution,
        baseId: execution.baseId,
        metadataBaseId: execution.executionMetadata?.baseId,
      });
      return;
    }
    navigate(`/a/${appId}/tf/${baseId}`);
  };

  const getEnvironmentDisplay = (environmentId?: string) => {
    if (!environmentId || !environments) return '-';
    // Match execution's environmentId with environment's id (after mapper transformation)
    const env = environments.find((e) => e.id === environmentId);
    if (!env) return environmentId;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            backgroundColor: env.color || '#404040',
            border: '1px solid #000',
            borderRadius: 0.5,
          }}
        />
        <Typography variant="body2" fontWeight="bold">
          {env.name}
        </Typography>
      </Box>
    );
  };

  // State for expanded execution row
  const [expandedExecutionId, setExpandedExecutionId] = useState<string | null>(null);
  const [executionLogs, setExecutionLogs] = useState<any[] | null>(null);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [downloadingExecutionId, setDownloadingExecutionId] = useState<string | null>(null);

  const handleRowClick = async (execution: ExecutionLog) => {
    const executionId = execution.externalExecutionId?.toString().replace(/\\+$/, '');

    // Toggle: if already expanded, collapse it
    if (expandedExecutionId === executionId) {
      setExpandedExecutionId(null);
      setExecutionLogs(null);
      return;
    }

    // Expand this execution
    setExpandedExecutionId(executionId || null);
    setIsLoadingLogs(true);
    setExecutionLogs(null);

    if (!executionId || !tenantId) {
      setIsLoadingLogs(false);
      return;
    }

    try {
      // Fetch logs using RTK Query
      const result = await dispatch(
        transformersApi.endpoints.getExecutionLogsByGuid.initiate(
          {
            tenantId,
            executionGuid: executionId,
          },
          { forceRefetch: true }
        )
      ).unwrap();

      setExecutionLogs(result || []);
    } catch (error) {
      logger.error('[Execution Logs] Error:', error);
      setExecutionLogs([]);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const handleDownloadLogs = async (execution: ExecutionLog) => {
    const executionId = String(execution.executionGUID || execution.id || '');

    try {
      setDownloadingExecutionId(executionId);

      // Format date as DDMMYYYY
      const finishTime = execution.finishTime || execution.startTime;
      if (!finishTime) {
        alert('No finish time available for this execution');
        return;
      }

      const splitStr = finishTime.split('T');
      const splitDate = splitStr[0].split('-');
      const formattedDate = splitDate[2] + splitDate[1] + splitDate[0]; // DDMMYYYY

      // Use transId as recipeId (matches dataProtect implementation)
      const recipeId = execution.transId;
      if (!recipeId) {
        alert('No transformer ID available for this execution');
        return;
      }

      const fileName = `${execution.executionGUID || execution.id}.zip`;

      // Step 1: Get the presigned S3 URL from the API using RTK Query
      const downloadUrl = await dispatch(
        transformersApi.endpoints.getExecutionLogsDownloadUrl.initiate(
          {
            tenantId,
            appId: appId!,
            envId: execution.environmentId!,
            date: formattedDate,
            recipeId: recipeId.toString(),
            fileName,
          },
          { forceRefetch: true }
        )
      ).unwrap();

      // Step 2: Download the file using direct navigation (bypasses CORS)
      // This works because presigned URLs allow unauthenticated access
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      logger.error('Error downloading execution logs:', error);

      let errorMessage = 'Failed to download execution logs. ';
      if (error && typeof error === 'object' && 'status' in error) {
        const err = error as { status?: number };
        if (err.status === 404) {
          errorMessage += 'Logs not found. They may not be available for this execution.';
        } else if (err.status === 403) {
          errorMessage += 'Access denied.';
        } else if (err.status === 401) {
          errorMessage += 'Authentication failed. Please log in again.';
        } else {
          errorMessage += 'Please try again.';
        }
      } else {
        errorMessage += 'Please try again.';
      }

      alert(errorMessage);
    } finally {
      setDownloadingExecutionId(null);
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secsRemainder = seconds % 60;

    if (hrs > 0) {
      return `${hrs}h ${mins}m ${Math.floor(secsRemainder)}s`;
    }
    if (mins > 0) {
      return `${mins}m ${Math.floor(secsRemainder)}s`;
    }
    // Only show 2 decimal places when seconds alone
    return `${secsRemainder.toFixed(2)}s`;
  };

  const handleStopExecution = async (execution: ExecutionLog) => {
    if (!execution.environmentId || !execution.transId) {
      showSnackbar('Cannot stop execution: missing environment or transformer ID', 'error');
      return;
    }

    const recipeName = execution.executionMetadata?.recipeName || execution.name || 'execution';

    if (!window.confirm(`Are you sure you want to stop "${recipeName}"?`)) {
      return;
    }

    try {
      await stopExecution({
        envId: String(execution.environmentId),
        transId: execution.transId,
      }).unwrap();
      showSnackbar('Execution stopped successfully', 'success');
      refetch(); // Refresh the list
    } catch (error) {
      logger.error('Error stopping execution:', error);
      showSnackbar('Failed to stop execution. Please try again.', 'error');
    }
  };

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error">Failed to load executions.</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading title="Executions" subtitle="Monitor recipe execution runs and their status" />

      {/* Recipe filter chip (from URL param) */}
      {recipeFilterFromUrl && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Recipe: ${transformers?.find((t) => String(t.baseId) === recipeFilterFromUrl)?.name || recipeFilterFromUrl}`}
            size="small"
            color="primary"
            variant="outlined"
            onDelete={() => navigate(`/a/${appId}/health`)}
          />
        </Box>
      )}

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          alignItems: 'flex-end',
          overflowX: 'auto',
          pb: 1,
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 4,
          },
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            variant="caption"
            color="text.primary"
            sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
          >
            Time Range
          </Typography>
          <ToggleButtonGroup
            value={timeFilter}
            exclusive
            onChange={(_, value) => value && setTimeFilter(value)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                backgroundColor: 'background.paper',
                '&:not(.Mui-selected)': {
                  backgroundColor: 'background.paper',
                },
              },
            }}
          >
            <ToggleButton value="hour">Past Hour</ToggleButton>
            <ToggleButton value="day">Past 24h</ToggleButton>
            <ToggleButton value="week">This Week</ToggleButton>
            <ToggleButton value="pastWeek">Past Week</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {timeFilter === 'custom' && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexShrink: 0 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.primary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                From
              </Typography>
              <DateTimePicker
                value={customDateRange.startDate}
                onChange={(date) => setCustomDateRange((prev) => ({ ...prev, startDate: date }))}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 180,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    },
                  },
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.primary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                To
              </Typography>
              <DateTimePicker
                value={customDateRange.endDate}
                onChange={(date) => setCustomDateRange((prev) => ({ ...prev, endDate: date }))}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 180,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    },
                  },
                }}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ flexShrink: 0 }}>
          <Typography
            variant="caption"
            color="text.primary"
            sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
          >
            Status
          </Typography>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_, value) => value && setStatusFilter(value)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                backgroundColor: 'background.paper',
                '&:not(.Mui-selected)': {
                  backgroundColor: 'background.paper',
                },
              },
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="success">
              <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, color: 'success.main' }} />
              Success
            </ToggleButton>
            <ToggleButton value="error">
              <ErrorIcon fontSize="small" sx={{ mr: 0.5, color: 'error.main' }} />
              Failed
            </ToggleButton>
            <ToggleButton value="partial">
              <WarningIcon fontSize="small" sx={{ mr: 0.5, color: 'warning.main' }} />
              Partial
            </ToggleButton>
            <ToggleButton value="running">
              <InlineLoader size={14} />
              <span style={{ marginLeft: '4px' }}>Running</span>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {!recipeFilterFromUrl && (
          <Box sx={{ minWidth: 200, flexShrink: 0 }}>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
            >
              Recipe
            </Typography>
            <Autocomplete
              size="small"
              options={sortedTransformers}
              getOptionLabel={(option) => option.name}
              value={
                selectedRecipeId
                  ? (sortedTransformers.find((t) => String(t.baseId) === selectedRecipeId) ?? null)
                  : null
              }
              onChange={(_, newValue) => {
                setSelectedRecipeId(newValue ? String(newValue.baseId) : null);
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Recipes"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.baseId === value.baseId}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={isManualRefreshing ? 'Refreshing…' : 'Refresh'}>
            <Box component="span" sx={{ display: 'inline-block' }}>
              <IconButton
                onClick={handleRefresh}
                disabled={isManualRefreshing}
                sx={{
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    backgroundColor: 'background.paper',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'background.paper',
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    animation: isManualRefreshing ? 'execRefreshSpin 0.8s linear infinite' : 'none',
                    '@keyframes execRefreshSpin': {
                      from: { transform: 'rotate(0deg)' },
                      to: { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', height: 40, flexShrink: 0 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">Auto Refresh is {autoRefresh ? 'on' : 'off'}</Typography>
            }
          />
        </Box>
      </Box>

      {/* Summary Statistics */}
      {executions && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 1.5, px: 2, minHeight: 88, '&:last-child': { pb: 1.5 } }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Success Rate
                      </Typography>
                      {isLoading ? (
                        <Skeleton width={50} height={32} />
                      ) : (
                        <Typography
                          variant="h5"
                          fontWeight={600}
                          color={
                            stats.successRate >= 80
                              ? 'success.main'
                              : stats.successRate >= 50
                                ? 'warning.main'
                                : 'error.main'
                          }
                        >
                          {stats.successRate.toFixed(1)}%
                        </Typography>
                      )}
                    </Box>
                    <PercentIcon sx={{ fontSize: 28, color: '#4CAF50', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 1.5, px: 2, minHeight: 88, '&:last-child': { pb: 1.5 } }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Avg Duration
                      </Typography>
                      {isLoading ? (
                        <Skeleton width={50} height={32} />
                      ) : (
                        <Typography variant="h5" fontWeight={600}>
                          {formatDuration(stats.avgDuration) || '-'}
                        </Typography>
                      )}
                    </Box>
                    <AccessTimeIcon sx={{ fontSize: 28, color: '#FF9800', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 1.5, px: 2, minHeight: 88, '&:last-child': { pb: 1.5 } }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Status Breakdown
                      </Typography>
                      {isLoading ? (
                        <Skeleton width={120} height={32} />
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          <Chip
                            label={`${stats.successCount} ok`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                          <Chip
                            label={`${stats.failedCount} fail`}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                          <Chip
                            label={`${stats.partialCount} partial`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                          <Chip
                            label={`${stats.runningCount} run`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          {filteredExecutions.length !== executions.length && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing {filteredExecutions.length} of {executions.length} executions (filtered)
            </Typography>
          )}
        </Box>
      )}

      {/* Content */}
      {isLoading && <SkeletonLoader variant="list" count={5} height={56} />}
      {!isLoading && filteredExecutions.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            backgroundColor: 'background.paper',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            No executions found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statusFilter !== 'all' || timeFilter !== 'all'
              ? 'Try adjusting your filters.'
              : 'Executions will appear here when recipes are run.'}
          </Typography>
        </Box>
      )}
      {!isLoading && filteredExecutions.length > 0 && (
        <>
          <TableContainer
            sx={{
              maxHeight: 'calc(100vh - 420px)',
              overflowX: 'auto',
              backgroundColor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 1100, tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 90, width: 90, backgroundColor: 'background.paper' }}>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortOrder : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ minWidth: 180, width: 180, backgroundColor: 'background.paper' }}
                  >
                    <TableSortLabel
                      active={sortField === 'startTime'}
                      direction={sortField === 'startTime' ? sortOrder : 'desc'}
                      onClick={() => handleSort('startTime')}
                    >
                      Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200, backgroundColor: 'background.paper' }}>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Recipe
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ minWidth: 120, width: 120, backgroundColor: 'background.paper' }}
                  >
                    Actions
                  </TableCell>
                  <TableCell
                    sx={{ minWidth: 140, width: 140, backgroundColor: 'background.paper' }}
                  >
                    <TableSortLabel
                      active={sortField === 'environmentId'}
                      direction={sortField === 'environmentId' ? sortOrder : 'asc'}
                      onClick={() => handleSort('environmentId')}
                    >
                      Environment
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 80, width: 80, backgroundColor: 'background.paper' }}>
                    Version
                  </TableCell>
                  <TableCell sx={{ minWidth: 80, width: 80, backgroundColor: 'background.paper' }}>
                    <TableSortLabel
                      active={sortField === 'recordsRead'}
                      direction={sortField === 'recordsRead' ? sortOrder : 'desc'}
                      onClick={() => handleSort('recordsRead')}
                    >
                      DPR
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ minWidth: 100, width: 100, backgroundColor: 'background.paper' }}
                  >
                    <TableSortLabel
                      active={sortField === 'timer'}
                      direction={sortField === 'timer' ? sortOrder : 'desc'}
                      onClick={() => handleSort('timer')}
                    >
                      Timer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200, backgroundColor: 'background.paper' }}>
                    Error
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedExecutions.map((exec) => {
                  const cleanExecutionId = exec.externalExecutionId?.toString().replace(/\\+$/, '');
                  const isExpanded = expandedExecutionId === cleanExecutionId;

                  return (
                    <React.Fragment key={exec.executionGUID || exec.id}>
                      <TableRow>
                        <TableCell sx={{ minWidth: 90, width: 90 }}>
                          <ExecutionStatusIcon execution={exec} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 180, width: 180 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.7 },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(exec);
                            }}
                          >
                            {exec.startTime
                              ? format(new Date(exec.startTime), 'MMM do, h:mm:ss a')
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            onClick={() => handleRecipeClick(exec)}
                          >
                            {exec.executionMetadata?.recipeName ||
                              exec.name ||
                              activeRecipeName ||
                              exec.baseId ||
                              'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 120, width: 120 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {getExecutionStatus(exec).status === 'running' ? (
                              <Tooltip title="Stop Execution">
                                <IconButton
                                  size="small"
                                  onClick={() => handleStopExecution(exec)}
                                  disabled={isStoppingExecution}
                                  color="error"
                                >
                                  <StopIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Edit Recipe">
                                <IconButton size="small" onClick={() => handleRecipeClick(exec)}>
                                  <SettingsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Download Logs">
                              <Box component="span" sx={{ display: 'inline-block' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadLogs(exec)}
                                  disabled={
                                    getExecutionStatus(exec).status === 'running' ||
                                    downloadingExecutionId === String(exec.executionGUID || exec.id)
                                  }
                                >
                                  {downloadingExecutionId ===
                                  String(exec.executionGUID || exec.id) ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DownloadIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Box>
                            </Tooltip>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const cleanId = exec.externalExecutionId
                                    ?.toString()
                                    .replace(/\\+$/, '');
                                  // Open in new tab
                                  window.open(
                                    `/a/${appId}/health?executionGuid=${cleanId}&env=${exec.environmentId}`,
                                    '_blank'
                                  );
                                }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140, width: 140 }}>
                          {getEnvironmentDisplay(exec.environmentId)}
                        </TableCell>
                        <TableCell sx={{ minWidth: 80, width: 80 }}>
                          <Typography variant="body2" color="text.secondary">
                            v{exec.recipeVersionNumber || 1}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 80, width: 80 }}>
                          <Typography variant="body2" color="text.secondary">
                            {exec.recordsRead != null ? exec.recordsRead.toLocaleString() : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 100, width: 100 }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatDuration(exec.timer)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {exec.errorMessage || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {/* Expanded logs row */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell
                            colSpan={9}
                            sx={{ py: 0, px: 0, borderBottom: 'none', backgroundColor: 'white' }}
                          >
                            <Collapse in timeout="auto" unmountOnExit>
                              <Box
                                sx={{ p: 2, width: '100%', maxWidth: '100%', overflowX: 'auto' }}
                              >
                                {isLoadingLogs ? (
                                  <CircularProgress size={24} />
                                ) : executionLogs && executionLogs.length > 0 ? (
                                  <Box sx={{ width: '100%' }}>
                                    {/* Header section with execution summary */}
                                    <Box
                                      sx={{
                                        mb: 0.125,
                                        p: '6px 8px 8px 5px',
                                        backgroundColor: 'white',
                                        borderBottom: '1px solid #fdd663',
                                        fontSize: '14px',
                                      }}
                                    >
                                      <strong>Execution Id:</strong> {exec.externalExecutionId}{' '}
                                      &nbsp;&nbsp;&nbsp;&nbsp;
                                      <strong>Base Id:</strong>&nbsp;
                                      <Link
                                        to={`/a/${appId}/tf/${exec.executionMetadata?.baseId || exec.baseId}`}
                                        style={{ color: '#1976d2', textDecoration: 'none' }}
                                      >
                                        {exec.executionMetadata?.baseId || exec.baseId}
                                      </Link>
                                      &nbsp;&nbsp;&nbsp;&nbsp;
                                      <strong>Trans Id:</strong> {exec.transId}{' '}
                                      &nbsp;&nbsp;&nbsp;&nbsp;
                                      <strong>Version:</strong> v{exec.recipeVersionNumber || 1}{' '}
                                      &nbsp;&nbsp;&nbsp;&nbsp;
                                      <strong>Type:</strong> {exec.triggerType || 'Manual'}{' '}
                                      &nbsp;&nbsp;&nbsp;&nbsp;
                                      <strong>Total Timer:</strong> {exec.timer?.toFixed(5)}{' '}
                                      &nbsp;&nbsp;&nbsp;&nbsp;
                                      <strong>History:</strong>&nbsp;
                                      <Link
                                        to={`/a/${appId}/health?recipe=${exec.executionMetadata?.baseId || exec.baseId}`}
                                        style={{ color: '#1976d2', textDecoration: 'none' }}
                                      >
                                        Click here
                                      </Link>
                                    </Box>

                                    {/* Column headers */}
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        mb: 0.125,
                                        p: '6px 8px 8px 5px',
                                        backgroundColor: '#e8e8e8',
                                        color: '#333',
                                        borderBottom: '1px solid #ddd',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                      }}
                                    >
                                      <Box sx={{ width: '30%', ml: '1%' }}>
                                        <span>Statement:</span>
                                      </Box>
                                      <Box sx={{ width: '42%', ml: '1%' }}>
                                        <span>Result:</span>
                                      </Box>
                                      <Box sx={{ width: '11%', ml: '1%' }}>
                                        <span>Rows Read:</span>
                                      </Box>
                                      <Box sx={{ width: '13%', ml: '1%' }}>
                                        <span>Timer:</span>
                                      </Box>
                                    </Box>

                                    {/* Logs content - filtered to stop after error (unless onError='continue') */}
                                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                      {(() => {
                                        // Apply sequential step filtering to match dataProtect behavior
                                        const filteredLogs: any[] = [];
                                        for (const log of executionLogs) {
                                          if (log.completed) {
                                            filteredLogs.push(log);
                                            // Stop on a failed statement (unless onError='continue')
                                            if (!log.succeeded && log.onError !== 'continue') {
                                              break;
                                            }
                                          }
                                        }
                                        return filteredLogs;
                                      })().map((log: any, index) => {
                                        const isSchema = log.statement?.includes('printschema');
                                        const isTable =
                                          !isSchema &&
                                          (log.statement?.includes('print') ||
                                            log.statement?.includes('PRINT'));
                                        const logKey = log.id || log.statement || `log-${index}`;

                                        const hasExtendedProps =
                                          log.extendedProperties && log.extendedProperties !== '';

                                        return (
                                          <Box
                                            key={logKey}
                                            sx={{
                                              display: 'flex',
                                              py: 1,
                                              px: 0,
                                              borderBottom: '1px solid #ddd',
                                              fontSize: '13px',
                                              backgroundColor: 'white',
                                              '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                              },
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: '30%',
                                                ml: '1%',
                                                wordWrap: 'break-word',
                                              }}
                                            >
                                              {log.statement}
                                            </Box>
                                            <Box
                                              sx={{
                                                width: '42%',
                                                ml: '1%',
                                                wordWrap: 'break-word',
                                                verticalAlign: 'bottom',
                                              }}
                                            >
                                              {isTable ? (
                                                <LogResultTable result={log.result || ''} />
                                              ) : isSchema ? (
                                                <pre
                                                  style={{
                                                    margin: 0,
                                                    padding: 0,
                                                    fontSize: '13px',
                                                  }}
                                                >
                                                  {log.result}
                                                </pre>
                                              ) : hasExtendedProps ? (
                                                <>
                                                  {log.result}{' '}
                                                  <Link
                                                    to={`/a/${appId}/health/sfjobs?step=${log.stepGuid}&env=${exec.environmentId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                      color: '#0066cc',
                                                      textDecoration: 'none',
                                                    }}
                                                  >
                                                    (details)
                                                  </Link>
                                                </>
                                              ) : (
                                                log.result || '-'
                                              )}
                                            </Box>
                                            <Box sx={{ width: '11%', ml: '1%' }}>
                                              {log.recordsRead > 0 ? log.recordsRead : ''}
                                            </Box>
                                            <Box sx={{ width: '13%', ml: '1%' }}>
                                              {log.timer ? log.timer.toFixed(5) : '-'}
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                ) : (
                                  <Typography variant="body2">No logs available</Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
            nextIconButtonProps={isLastServerPage ? { disabled: true } : undefined}
            labelDisplayedRows={hasUnknownTotal ? ({ from, to }) => `${from}–${to}` : undefined}
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 1,
              mt: 0.5,
            }}
          />
        </>
      )}
    </PageContainer>
  );
};
